import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";

const About = () => {
  return (
    <div className="bg-[#0f172a] min-h-screen pt-24 pb-16 text-slate-100 overflow-hidden relative">
      <Helmet>
        <title>About Us - SyncMovie</title>
        <meta name="description" content="Learn more about SyncMovie, the platform designed to bring friends closer through synchronized watching." />
      </Helmet>

      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/10 blur-[120px] rounded-full -z-10"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/10 blur-[120px] rounded-full -z-10"></div>

      <div className="container mx-auto px-6">
        <motion.div 
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center mb-16">
            <motion.h1 
              className="text-5xl md:text-6xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-400"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              Our Story
            </motion.h1>
            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mx-auto">
              Bridging distances through the magic of cinema and real-time synchronization.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7 space-y-8">
              <motion.div 
                className="bg-slate-800/40 backdrop-blur-xl p-8 rounded-3xl border border-slate-700/50 shadow-2xl"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold mb-4 text-blue-400">The Vision</h2>
                <p className="text-slate-300 leading-relaxed text-lg">
                  SyncMovie was born from a simple realization: physical distance should never hinder shared experiences. 
                  Whether you're miles apart or just in different rooms, the joy of a movie premiere or a classic rewatch is amplified when shared.
                </p>
              </motion.div>

              <motion.div 
                className="bg-slate-800/40 backdrop-blur-xl p-8 rounded-3xl border border-slate-700/50 shadow-2xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <h2 className="text-2xl font-bold mb-4 text-purple-400">Our Mission</h2>
                <p className="text-slate-300 leading-relaxed text-lg">
                  We are building a vibrant community of movie lovers, united by a passion for synchronized storytelling. 
                  Our goal is to innovate the digital social experience, making every watch party seamless, interactive, and unforgettable.
                </p>
              </motion.div>
            </div>

            <div className="md:col-span-5">
              <motion.div 
                className="relative group p-2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl blur-2xl opacity-20 group-hover:opacity-40 transition-opacity"></div>
                <img
                  src="https://images.unsplash.com/photo-1532009877282-3340270e0529?auto=format&fit=crop&w=800&q=80"
                  alt="About Us"
                  className="rounded-3xl shadow-2xl relative z-10 w-full object-cover aspect-[4/5] object-center border border-slate-700 hover:scale-[1.02] transition-transform duration-500"
                />
              </motion.div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 text-center">
            {[
              { label: "Active Users", value: "20K+" },
              { label: "Movies Played", value: "150K+" },
              { label: "Hours Synced", value: "500K+" },
              { label: "Countries", value: "120+" }
            ].map((stat, i) => (
              <motion.div 
                key={i}
                className="p-6 bg-slate-800/20 rounded-2xl border border-slate-700/30"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + (i * 0.1) }}
              >
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-slate-500 font-medium uppercase tracking-wider">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
